import { useEffect, useMemo, useRef } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useLogs } from '../../../api/useUnwindDebug';
import { LogQuery, UnwindLog } from '../../../types/unwind-debug.types';
import { LogRow } from './LogRow';

export interface LogsViewProps {
  query: LogQuery;
  liveEvents: UnwindLog[];
  liveTail: boolean;
}

export function LogsView({ query, liveEvents, liveTail }: LogsViewProps) {
  const { data, isFetching, fetchNextPage, hasNextPage, refetch } = useLogs(query, { enabled: true });

  const pageItems = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data]
  );

  const items = useMemo(() => {
    if (!liveTail) return pageItems;
    const seen = new Set<string>();
    const out: UnwindLog[] = [];
    for (const ev of liveEvents) {
      if (!ev._id || seen.has(ev._id)) continue;
      seen.add(ev._id);
      out.push(ev);
    }
    for (const ev of pageItems) {
      if (!ev._id || seen.has(ev._id)) continue;
      seen.add(ev._id);
      out.push(ev);
    }
    return out;
  }, [liveTail, liveEvents, pageItems]);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (liveTail) return;
    const t = setInterval(() => refetch(), 5_000);
    return () => clearInterval(t);
  }, [liveTail, refetch]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          bgcolor: 'rgba(0,0,0,0.2)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 1,
        }}
      >
        {items.length === 0 && !isFetching && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'grey.500' }}>
            <Typography>No log entries for this filter.</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Default level is <strong>warn</strong>. Enable debug mode for the selected pair to capture info+debug.
            </Typography>
          </Box>
        )}
        {items.map((l) => (
          <LogRow key={l._id} log={l} />
        ))}
        {hasNextPage && !liveTail && (
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Button size="small" onClick={() => fetchNextPage()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={14} /> : 'Load more'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
